import type { CardDef } from "../types";
import { useLanguage } from "@/providers/LanguageProvider";

interface LanguageCardProps {
  card: CardDef;
  value: string;
  onValue: (v: "en" | "es") => void;
  onNext: () => void;
  index: number;
}

export function LanguageCard({
  card,
  value,
  onValue,
  onNext,
}: LanguageCardProps) {
  const { setLocale } = useLanguage();

  const handleSelect = async (lang: "en" | "es") => {
    onValue(lang);
    try {
      await setLocale(lang);
    } catch {
      // ignore
    }
    setTimeout(() => onNext(), 300);
  };

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-xl mx-auto">
        {(card.options ?? []).map((opt) => {
          const isSelected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value as "en" | "es")}
              className={`group relative flex flex-col items-center justify-center p-6 sm:p-10 rounded-[1.25rem] sm:rounded-[2rem] border-2 transition-all duration-300 ease-out outline-none focus-visible:ring-4 focus-visible:ring-[#166B93]/30 ${
                isSelected
                  ? "border-[#166B93] bg-[#166B93]/[0.03] scale-[1.02] shadow-[0_8px_30px_rgb(22,107,147,0.12)]"
                  : "border-transparent bg-white shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 hover:border-gray-200"
              }`}
            >
              <span className="mb-3 sm:mb-5 transform transition-transform duration-300 group-hover:scale-110 drop-shadow-sm flex items-center justify-center h-[72px] sm:h-[96px]">
                {opt.value === "en" ? (
                  <img src="https://flagcdn.com/w80/us.png" srcSet="https://flagcdn.com/w160/us.png 2x" alt="US Flag" className="w-16 sm:w-20 rounded shadow-sm object-cover" />
                ) : opt.value === "es" ? (
                  <img src="https://flagcdn.com/w80/es.png" srcSet="https://flagcdn.com/w160/es.png 2x" alt="Spain Flag" className="w-16 sm:w-20 rounded shadow-sm object-cover" />
                ) : (
                  <span className="text-5xl sm:text-7xl">{opt.emoji}</span>
                )}
              </span>
              <span className={`text-xl sm:text-2xl font-bold tracking-wide transition-colors ${
                isSelected ? "text-[#166B93]" : "text-gray-700 group-hover:text-gray-900"
              }`}>
                {opt.label}
              </span>
              
              {isSelected && (
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-[#166B93] text-white p-1.5 rounded-full shadow-md animate-in zoom-in duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
